import { createRoot } from 'react-dom/client';
import { loadRuntimeConfig } from '@/lib/runtimeConfig';
import './styles/index.css';

// V1 parity: fetch /config.json BEFORE any module that calls getConfig()
// loads. V1's lib/constants.ts (used by ~30 files) reads config at
// module-load time, which means everything below MUST be a dynamic
// import — static imports up here would evaluate too early and crash.
async function boot() {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element #root not found in index.html');
  }

  try {
    await loadRuntimeConfig();
  } catch (err) {
    createRoot(rootElement).render(
      <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
        <h1>Could not load runtime configuration</h1>
        <p>
          Check container env vars and <code>/config.json</code>.
        </p>
        <pre style={{ whiteSpace: 'pre-wrap' }}>{String(err)}</pre>
      </div>,
    );
    return;
  }

  // Dynamic imports — only safe to evaluate now that config is cached.
  const [
    { StrictMode },
    { QueryClientProvider },
    { RouterProvider },
    { TooltipProvider },
    { AuthProvider },
    { AppProvider },
    { WebSocketProvider },
    { NavigationProvider },
    { FiltersProvider },
    { ThemeProvider },
    { ChatSetupProvider },
    { KeysReadinessProvider },
    { KeysRequiredGate },
    { WelcomeToV2Modal },
    { RootErrorBoundary },
    { queryClient },
    { router },
  ] = await Promise.all([
    import('react'),
    import('@tanstack/react-query'),
    import('@tanstack/react-router'),
    import('@/components/ui/tooltip'),
    import('@/components/AuthProvider'),
    import('@/context/AppContext'),
    import('@/context/WebSocketContext'),
    import('@/context/NavigationContext'),
    import('@/context/FilterContext'),
    import('@/context/ThemeContext'),
    import('@/context/ChatSetupContext'),
    import('@/context/KeysReadinessContext'),
    import('@/components/KeysRequiredGate'),
    import('@/components/WelcomeToV2Modal'),
    import('@/components/RootErrorBoundary'),
    import('@/lib/queryClient'),
    import('@/routes/router'),
  ]);

  // V1 parity — same provider stack V1 wraps App in, so V1 pages can
  // call useNavigation / useAppContext / useChatSetupStatus / etc.
  createRoot(rootElement).render(
    <StrictMode>
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ThemeProvider>
              <AppProvider>
                <ChatSetupProvider>
                  <KeysReadinessProvider>
                    <WebSocketProvider>
                      <NavigationProvider>
                        <FiltersProvider>
                          <TooltipProvider>
                            <RouterProvider router={router} />
                            {/*
                              ⚠️ These sibling modals sit OUTSIDE
                              <RouterProvider>. Any component you add
                              here (or edit) MUST NOT call tanstack
                              router hooks (useLocation, useNavigate,
                              useSearch, useParams, useRouter). Those
                              hooks read from the router store which is
                              null outside its provider → the whole app
                              crashes at load with
                              "Cannot read properties of null (reading
                              'stores')" (see git 2026-09-03). If a
                              modal needs path / navigation, use
                              window.location — the top-level overlay
                              UX is fine with a full-page reset.
                            */}
                            <WelcomeToV2Modal />
                            <KeysRequiredGate />
                          </TooltipProvider>
                        </FiltersProvider>
                      </NavigationProvider>
                    </WebSocketProvider>
                  </KeysReadinessProvider>
                </ChatSetupProvider>
              </AppProvider>
            </ThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </RootErrorBoundary>
    </StrictMode>,
  );
}

void boot();
